// 数据同步 - 总览屏。
//
// 列出所有已配置的上游数据源（目前只有 zzshu）。每张卡片显示：
//   - 上次同步时间
//   - 已镜像的 commodity / category 数
//   - 跳转到该源的明细页（zzshu → /data-sync/zzshu）
//
// 当 fuelio-api 的 DB_CRAWLER binding 未配置（生产 D1 还没 create），后端会
// 退化返回 stats: null，本屏会显示"待初始化"。

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { RefreshCw, Database, ChevronRight } from 'lucide-react'
import { api, errorMessage, type DataSyncSource } from '@/lib/api'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { useToast } from '@/components/ui/Toast'
import { PageError, PageLoading } from './Stats'

const SOURCE_ROUTE: Record<string, string> = {
  zzshu:   '/data-sync/zzshu',
  aibijia: '/data-sync/aibijia',
}

function fmtRelative(unix: number | null): string {
  if (!unix) return '—'
  const diffSec = Math.floor(Date.now() / 1000) - unix
  if (diffSec < 60) return `${diffSec} 秒前`
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} 分钟前`
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} 小时前`
  return `${Math.floor(diffSec / 86400)} 天前`
}

export function DataSyncScreen() {
  const toast = useToast()
  const [sources, setSources] = useState<DataSyncSource[] | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const load = async () => {
    setLoading(true); setErr(null)
    try {
      const r = await api.dataSyncSources()
      setSources(r.sources)
    } catch (e) {
      setErr(errorMessage(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void load() }, [])

  if (loading && !sources) return <PageLoading/>
  if (err && !sources) return <PageError msg={err}/>

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">数据同步</h1>
          <p className="text-sm text-muted-foreground mt-1">
            来自第三方平台的商品镜像。所有数据保存在独立的 <code className="text-xs bg-muted px-1.5 py-0.5 rounded">fuelio-crawler-db</code> 中，
            不会污染主商品库。导入到 fuelio 上架前可在各源详情页预览。
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()}>
          <RefreshCw className="w-4 h-4 mr-2"/>刷新
        </Button>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {(sources ?? []).map((s) => (
          <Link key={s.code} to={SOURCE_ROUTE[s.code] ?? '#'} className="block">
            <Card className="p-5 hover:border-primary/50 transition cursor-pointer h-full">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-2">
                  <Database className="w-5 h-5 text-primary"/>
                  <h3 className="font-medium">{s.name}</h3>
                </div>
                <Badge tone={s.status === 'active' ? 'success' : 'default'}>
                  {s.status === 'active' ? '启用' : '暂停'}
                </Badge>
              </div>

              <p className="text-xs text-muted-foreground mb-4 line-clamp-2 min-h-[2rem]">
                {s.description}
              </p>

              {s.stats ? (
                <div className="space-y-1.5 text-xs">
                  {/* 不同源对两个 count 语义略不同：
                     - zzshu   : commodityCount = 商品数 / categoryCount = 品类数
                     - aibijia : commodityCount = offers 总数 / categoryCount = 产品大类数（4 = ChatGPT/Gemini/Claude/Grok）
                     底层 stat 字段沿用 commodityCount/categoryCount 命名以节省字段，文案这里按 source 切。 */}
                  {(() => {
                    const labels = s.code === 'aibijia'
                      ? { items: 'Offers 数', groups: '产品大类' }
                      : { items: '商品数',   groups: '品类数' }
                    return (
                      <>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">{labels.items}</span>
                          <span className="font-mono">{s.stats.commodityCount}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">{labels.groups}</span>
                          <span className="font-mono">{s.stats.categoryCount}</span>
                        </div>
                      </>
                    )
                  })()}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">上次同步</span>
                    <span className="font-mono">{fmtRelative(s.stats.lastSyncedAt)}</span>
                  </div>
                </div>
              ) : (
                <div className="text-xs text-amber-600 dark:text-amber-400">
                  ⚠ 镜像库未初始化或表不存在，请先运行 import-skus 脚本。
                </div>
              )}

              <div className="mt-4 flex items-center justify-between text-xs">
                <a
                  href={s.baseUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={(e) => e.stopPropagation()}
                >
                  {s.baseUrl.replace(/^https?:\/\//, '')} ↗
                </a>
                <ChevronRight className="w-4 h-4 text-muted-foreground"/>
              </div>
            </Card>
          </Link>
        ))}
      </div>

      {sources && sources.length === 0 && (
        <Card className="p-8 text-center text-muted-foreground">
          <p>尚未配置任何数据源。后续在 <code>apps/api/src/routes/admin/data-sync.ts</code> 的 SOURCES 列表里添加。</p>
        </Card>
      )}
    </div>
  )
}
